<script setup lang="ts">
import { WEB_PLAN_USD } from '~/utils/billing/webPlanPrices';
import { loadScript, type PayPalButtonsComponent } from '@paypal/paypal-js';
import { useQueryClient } from '@tanstack/vue-query';
import { useStripeJs, type StripeElement, type StripeElements, type StripeJsInstance } from '~/composables/useStripeJs';
import { queries } from '~/keys';
import { scrollStartOffset } from '~/utils/i18n/scroll';
import type { BillingMe } from '~/api/queries/billing';
import { purchaseEventId } from '~/utils/tracking/purchaseEventId';

type Step = 'plans' | 'checkout' | 'success';
type BillingType = 'ANNUAL' | 'MONTHLY';
type PlanType = 'MEMBER' | 'PRO' | 'TEAM';
type CellData = 'ck' | 'dash' | string;

const { t, locale } = useI18n();
const { percent: topSplitPercent } = useTopSplitPercent();
const { formatCurrency } = useFormat();

// ─── Static data ────────────────────────────────────────────────────────────

// Columns: [feature, Member, Pro, Team]. Inventory / news / briefings / live
// sessions are the Pro "information edge" (moved up from the old free tier), so
// Member shows a dash on those — that gap is the Member→Pro upsell. Kept as a
// computed (not a plain const) so an in-place locale switch on this page
// re-renders every cell in the new language.
const TABLE_ROWS = computed<Array<[string, CellData, CellData, CellData]>>(() => [
  [t('billing.pricingModal.compare.dealRegistration'), 'ck', 'ck', 'ck'],
  [t('billing.pricingModal.compare.commissionTracking'), 'ck', 'ck', 'ck'],
  [t('billing.pricingModal.compare.dealDeskSupport'), 'ck', 'ck', 'ck'],
  [t('billing.pricingModal.compare.addManageTrackDeals'), 'ck', 'ck', 'ck'],
  [t('billing.pricingModal.compare.earningsDashboard'), 'ck', 'ck', 'ck'],
  [
    t('billing.pricingModal.compare.dedicatedSuccessManager'),
    'dash',
    'ck',
    t('billing.pricingModal.compare.everySeat'),
  ],
  [t('billing.pricingModal.compare.liveDubaiInventory'), 'dash', 'ck', 'ck'],
  [t('billing.pricingModal.compare.newsOffersAlerts'), 'dash', 'ck', 'ck'],
  [t('billing.pricingModal.compare.developerBriefings'), 'dash', 'ck', 'ck'],
  [t('billing.pricingModal.compare.liveSessions'), 'dash', 'ck', 'ck'],
  [
    t('billing.pricingModal.compare.aiChatQueries'),
    t('billing.pricingModal.compare.aiChatMemberValue'),
    t('billing.pricingModal.compare.unlimitedFootnote'),
    t('billing.pricingModal.compare.unlimitedFootnote'),
  ],
  [
    t('billing.pricingModal.compare.marketingDesigns'),
    t('billing.pricingModal.compare.marketingMemberValue'),
    t('billing.pricingModal.compare.unlimitedFootnote'),
    t('billing.pricingModal.compare.unlimitedFootnote'),
  ],
  [
    t('billing.pricingModal.compare.masterclassAccess'),
    t('billing.pricingModal.compare.full'),
    t('billing.pricingModal.compare.full'),
    t('billing.pricingModal.compare.fullAllSeats'),
  ],
  [
    t('billing.pricingModal.compare.payoutSpeed'),
    t('billing.pricingModal.compare.payoutMember'),
    t('billing.pricingModal.compare.payoutProTeam'),
    t('billing.pricingModal.compare.payoutTeamAllSeats'),
  ],
  [t('billing.pricingModal.compare.seats'), '1', '1', t('billing.pricingModal.compare.seatsTeamValue')],
]);

const FAQ_ITEMS = computed<Array<[string, string]>>(() => [
  [t('billing.pricingModal.faq.q1'), t('billing.pricingModal.faq.a1')],
  [t('billing.pricingModal.faq.q2'), t('billing.pricingModal.faq.a2', { percent: topSplitPercent.value })],
  [t('billing.pricingModal.faq.q3'), t('billing.pricingModal.faq.a3')],
  [t('billing.pricingModal.faq.q4'), t('billing.pricingModal.faq.a4')],
  [t('billing.pricingModal.faq.q5'), t('billing.pricingModal.faq.a5')],
  [t('billing.pricingModal.faq.q6'), t('billing.pricingModal.faq.a6')],
  [t('billing.pricingModal.faq.q7'), t('billing.pricingModal.faq.a7')],
  [t('billing.pricingModal.faq.q8'), t('billing.pricingModal.faq.a8')],
]);

// ─── State ───────────────────────────────────────────────────────────────────

const { open, initialInterval, initialPlan, close } = useBillingModal();
const { planLabel, provider, isTeamOwner, storeManagedBy, storeManageUrl } = useEntitlements();

const authStore = useAuthStore();
const route = useRoute();
const config = useRuntimeConfig();
const qc = useQueryClient();
const { showSnackBanner } = useUIStore();
const { loadStripe } = useStripeJs();
const { sendEventToBackend } = useTracking();

const step = ref<Step>('plans');
// Seeded from the opener (Monthly by default) so the first paint already shows the right tab.
const billingInterval = ref<BillingType>(initialInterval.value);
const selectedPlan = ref<{ plan: PlanType; interval: BillingType } | null>(null);

// Checkout / payment mount refs
const paymentMountRef = ref<HTMLDivElement | null>(null);
const paypalMountRef = ref<HTMLDivElement | null>(null);

// Checkout flow state
const checkoutLoading = ref(false);
// Errors hold a message key (or a provider-supplied text) and are translated at
// render time, so an in-place locale switch updates them.
const checkoutErrorKey = ref<string | null>(null);
const payError = ref<{ key: string } | { text: string } | null>(null);
const payErrorText = computed(() => {
  if (!payError.value) return null;
  return 'key' in payError.value ? t(payError.value.key) : payError.value.text;
});
const paying = ref(false);
const paypalAvailable = ref(false);

// Set by startCheckout from /api/billing/subscribe — the conversion value and the
// Stripe subscription id the Meta Purchase event_id is derived from.
let checkoutAmountUsd: number | null = null;
let stripeSubscriptionId: string | null = null;

// Non-reactive payment SDK handles — they are not rendered, so they stay out of
// refs. Recreated per checkout entry and disposed on exit to avoid double-mount.
let stripe: StripeJsInstance | null = null;
let elements: StripeElements | null = null;
let paymentElement: StripeElement | null = null;
let paypalButtons: PayPalButtonsComponent | null = null;

// Toggle knob
const annualBtnRef = ref<HTMLButtonElement | null>(null);
const monthlyBtnRef = ref<HTMLButtonElement | null>(null);
const knobStyle = ref<{ width: string; transform: string }>({
  width: '0px',
  transform: 'translateX(0px)',
});

// Mobile carousel
const cardGridRef = ref<HTMLElement | null>(null);
const activeDot = ref(0);

// ─── Computed ────────────────────────────────────────────────────────────────

// Annual/Monthly toggle (default Monthly, see useBillingModal). Member/Pro have both intervals; Team is
// annual only, so its CTA always checks out ANNUAL.
const MEMBER_ANNUAL_TOTAL = WEB_PLAN_USD.MEMBER.ANNUAL;
const PRO_ANNUAL_TOTAL = WEB_PLAN_USD.PRO.ANNUAL;
const TEAM_ANNUAL_TOTAL = WEB_PLAN_USD.TEAM.ANNUAL;
const MEMBER_MONTHLY_PRICE = WEB_PLAN_USD.MEMBER.MONTHLY;
const PRO_MONTHLY_PRICE = WEB_PLAN_USD.PRO.MONTHLY;
const MONTHS_PER_YEAR = 12;
const TEAM_SEATS_INCLUDED = 5;

const isAnnual = computed(() => billingInterval.value === 'ANNUAL');
const perPeriodText = computed(() =>
  isAnnual.value ? t('billing.pricingModal.perYear') : t('billing.pricingModal.perMonth'),
);
// Monthly price is fixed ($29 / $69); the annual toggle shows the YEARLY total
// ($289 / $689) — never a derived per-month figure.
const memberPrice = computed(() => formatCurrency(isAnnual.value ? MEMBER_ANNUAL_TOTAL : MEMBER_MONTHLY_PRICE, 'USD'));
const proPrice = computed(() => formatCurrency(isAnnual.value ? PRO_ANNUAL_TOTAL : PRO_MONTHLY_PRICE, 'USD'));

// Constant integer inputs, so `maximumFractionDigits: 0` (useFormat's default) always
// renders the same digits as the old bespoke `$${amount}` string for `en`.
const approxPerMonthAmount = (annual: number) => formatCurrency(Math.round(annual / MONTHS_PER_YEAR), 'USD');
// Annual: rounded monthly-equivalent "2 months free" reassurance; monthly: the yearly alternative.
const billedTextFor = (annualTotal: number) =>
  isAnnual.value
    ? t('billing.pricingModal.billedApprox', { amount: approxPerMonthAmount(annualTotal) })
    : t('billing.pricingModal.billedOrYear', { amount: formatCurrency(annualTotal, 'USD') });
const memberBilledText = computed(() => billedTextFor(MEMBER_ANNUAL_TOTAL));
const proBilledText = computed(() => billedTextFor(PRO_ANNUAL_TOTAL));
const teamBilledText = computed(() =>
  t('billing.pricingModal.billedApproxTeam', {
    amount: approxPerMonthAmount(TEAM_ANNUAL_TOTAL),
    seats: TEAM_SEATS_INCLUDED,
  }),
);
const saveBadgeText = computed(() => t('billing.pricingModal.saveBadge'));
const memberComparePrice = computed(() =>
  t('billing.pricingModal.priceMonthYear', {
    monthly: formatCurrency(MEMBER_MONTHLY_PRICE, 'USD'),
    annual: formatCurrency(MEMBER_ANNUAL_TOTAL, 'USD'),
  }),
);
const proComparePrice = computed(() =>
  t('billing.pricingModal.priceMonthYear', {
    monthly: formatCurrency(PRO_MONTHLY_PRICE, 'USD'),
    annual: formatCurrency(PRO_ANNUAL_TOTAL, 'USD'),
  }),
);
const closingCtaText = computed(() =>
  isAnnual.value
    ? t('billing.pricingModal.closing.ctaGo', { amount: formatCurrency(PRO_ANNUAL_TOTAL, 'USD') })
    : t('billing.pricingModal.closing.ctaGoMonthly', { amount: formatCurrency(PRO_MONTHLY_PRICE, 'USD') }),
);

const isMemberCurrent = computed(() => planLabel.value === 'Member');
const isProCurrent = computed(() => planLabel.value === 'Pro');
const isTeamCurrent = computed(() => planLabel.value === 'Team');

const checkoutSummary = computed(() => {
  if (!selectedPlan.value) return null;
  const { plan, interval } = selectedPlan.value;
  const annual = interval === 'ANNUAL';
  if (plan === 'MEMBER') {
    return {
      name: t('billing.pricingModal.member.tier'),
      interval: annual ? t('billing.pricingModal.checkout.annual') : t('billing.pricingModal.checkout.monthly'),
      price: annual
        ? t('billing.pricingModal.checkout.priceAnnual', { amount: formatCurrency(MEMBER_ANNUAL_TOTAL, 'USD') })
        : t('billing.pricingModal.checkout.priceMonthly', { amount: formatCurrency(MEMBER_MONTHLY_PRICE, 'USD') }),
      features: [
        t('billing.pricingModal.checkout.memberFeat1'),
        t('billing.pricingModal.checkout.memberFeat2'),
        // The full masterclass comes with annual plans only.
        annual ? t('billing.pricingModal.checkout.memberFeat3') : t('billing.pricingModal.checkout.memberFeat3Monthly'),
        t('billing.pricingModal.checkout.memberFeat4'),
      ],
    };
  }
  if (plan === 'PRO') {
    return {
      name: t('billing.pricingModal.pro.tier'),
      interval: annual ? t('billing.pricingModal.checkout.annual') : t('billing.pricingModal.checkout.monthly'),
      price: annual
        ? t('billing.pricingModal.checkout.priceAnnual', { amount: formatCurrency(PRO_ANNUAL_TOTAL, 'USD') })
        : t('billing.pricingModal.checkout.priceMonthly', { amount: formatCurrency(PRO_MONTHLY_PRICE, 'USD') }),
      features: [
        t('billing.pricingModal.checkout.proFeat1'),
        t('billing.pricingModal.checkout.proFeat2'),
        t('billing.pricingModal.checkout.proFeat3'),
        t('billing.pricingModal.checkout.proFeat4'),
        t('billing.pricingModal.checkout.proFeat5'),
      ],
    };
  }
  return {
    name: t('billing.pricingModal.team.tier'),
    interval: t('billing.pricingModal.checkout.annual'),
    price: t('billing.pricingModal.checkout.priceAnnual', { amount: formatCurrency(TEAM_ANNUAL_TOTAL, 'USD') }),
    features: [
      t('billing.pricingModal.checkout.teamFeat1'),
      t('billing.pricingModal.checkout.teamFeat2'),
      t('billing.pricingModal.checkout.teamFeat3'),
      t('billing.pricingModal.checkout.teamFeat4'),
    ],
  };
});

// ─── Toggle knob ─────────────────────────────────────────────────────────────

// Matches `.pm-toggle` padding / `.pm-knob` left inset in the styles below.
const TOGGLE_PADDING_PX = 4;

// offsetLeft is physical, so the knob stays physically anchored (`left`) and
// tracks the active button in both LTR and RTL.
const updateKnob = () => {
  const btn = billingInterval.value === 'ANNUAL' ? annualBtnRef.value : monthlyBtnRef.value;
  if (!btn) return;
  knobStyle.value = {
    width: `${btn.offsetWidth}px`,
    transform: `translateX(${btn.offsetLeft - TOGGLE_PADDING_PX}px)`,
  };
};

watch(open, async (val) => {
  if (val) {
    // Always reset to the plan picker first (this also tears down any leftover
    // checkout via the step watcher) before optionally jumping to a preset plan.
    step.value = 'plans';
    selectedPlan.value = null;
    billingInterval.value = initialInterval.value;
    await nextTick();
    updateKnob();
    // Admin `?upgrade=` deep-link: land straight in checkout for the given plan.
    if (initialPlan.value) {
      selectPlan(initialPlan.value.plan, initialPlan.value.interval);
    }
  } else {
    teardownCheckout();
  }
});

// Button widths change with the interval and with an in-place locale switch.
watch([billingInterval, locale], async () => {
  await nextTick();
  updateKnob();
});

// ─── Mobile carousel ─────────────────────────────────────────────────────────

const onGridScroll = () => {
  const grid = cardGridRef.value;
  if (!grid) return;
  const max = grid.scrollWidth - grid.clientWidth;
  if (max <= 0) return;
  activeDot.value = Math.min(2, Math.round((scrollStartOffset(grid) / max) * 2));
};

// ─── Actions ─────────────────────────────────────────────────────────────────

const authHeaders = () => ({ Authorization: `Bearer ${authStore.token}` });

const selectPlan = (plan: PlanType, interval: BillingType) => {
  // A plan billed by the App Store / Google Play changes there, never through a web checkout.
  if (storeManagedBy.value) return;
  void sendEventToBackend('checkout_started', { plan, interval });
  selectedPlan.value = { plan, interval };
  step.value = 'checkout';
};

// Meta InitiateCheckout (sGTM "Meta - Initiate Checkout"): the payment form is
// open with a real price. Meta-only event name, so email/phone stay out of GA4
// (GA4 gets checkout_started from selectPlan).
const trackInitiateCheckout = (subscriptionId: string | null) => {
  const user = authStore.user;
  void sendEventToBackend(
    'initiate_checkout_meta',
    {
      crm_user_id: user?.id,
      email: user?.email,
      phone: user?.phone,
      fname: user?.firstName,
      lname: user?.lastName,
      amount: checkoutAmountUsd ?? 0,
      currency: 'USD',
      plan: selectedPlan.value?.plan,
      interval: selectedPlan.value?.interval,
    },
    subscriptionId ? { event_id: `checkout_${subscriptionId}` } : {},
  );
};

// Browser-side Purchase → sGTM "Meta - Subscription Purchase" tag (Meta only —
// no GA4 tag listens, so email/phone stay out of GA4; GA4 gets subscription_paid).
// The payment webhook sends the server-side twin with the same event_id, so Meta
// counts the sale once whichever arrives (the webhook covers closed tabs).
const trackPurchase = (subscriptionId: string | null, paymentMethod: 'stripe' | 'paypal') => {
  const user = authStore.user;
  void sendEventToBackend(
    'subscription_purchase',
    {
      crm_user_id: user?.id,
      email: user?.email,
      phone: user?.phone,
      fname: user?.firstName,
      lname: user?.lastName,
      amount: checkoutAmountUsd ?? 0,
      currency: 'USD',
      plan: selectedPlan.value?.plan,
      interval: selectedPlan.value?.interval,
      payment_method: paymentMethod,
    },
    subscriptionId ? { event_id: purchaseEventId(subscriptionId) } : {},
  );
};

// Poll /api/billing/me until the subscription reads active/trialing (or tools
// are unlimited). The webhook is the real authority — this only advances the UI.
const pollBillingUntilActive = async (maxMs = 20_000, intervalMs = 1_500): Promise<boolean> => {
  const start = Date.now();
  while (Date.now() - start < maxMs) {
    try {
      const res = await $fetch<{ success: boolean; data: BillingMe }>('/api/billing/me', {
        headers: authHeaders(),
      });
      const status = res?.data?.subscriptionStatus ?? '';
      if (['active', 'trialing'].includes(status) || res?.data?.entitlements?.unlimitedTools) {
        return true;
      }
    } catch {
      // ignore — retry until timeout
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return false;
};

const mountStripe = async (clientSecret: string) => {
  stripe = await loadStripe();
  if (!stripe) throw new Error('Stripe failed to load');
  elements = stripe.elements({
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#D4A032',
        colorBackground: '#ffffff',
        colorText: '#0D0F15',
        fontFamily: 'Manrope, system-ui, sans-serif',
        borderRadius: '10px',
      },
    },
  });
  paymentElement = elements.create('payment', { layout: { type: 'tabs', defaultCollapsed: false } });
  await nextTick();
  if (paymentMountRef.value) paymentElement.mount(paymentMountRef.value);
};

const paypalPlanIdFor = (paypal: {
  proMonthlyPlanId: string;
  proAnnualPlanId: string;
  teamAnnualPlanId: string;
}): string => {
  if (!selectedPlan.value) return '';
  const { plan, interval } = selectedPlan.value;
  if (plan === 'TEAM') return paypal.teamAnnualPlanId;
  // No Member PayPal plan exists (only Pro monthly/annual + Team annual) — never
  // fall through to a Pro plan id; '' hides the PayPal option.
  if (plan === 'MEMBER') return '';
  return interval === 'ANNUAL' ? paypal.proAnnualPlanId : paypal.proMonthlyPlanId;
};

const mountPaypal = async () => {
  paypalAvailable.value = false;
  const clientId = config.public.paypalClientId as string | undefined;
  if (!clientId || !selectedPlan.value) return;

  const prices = await $fetch<{
    success: boolean;
    data: { paypal: { proMonthlyPlanId: string; proAnnualPlanId: string; teamAnnualPlanId: string } };
  }>('/api/billing/prices');
  const planId = paypalPlanIdFor(prices.data.paypal);
  if (!planId) return; // No PayPal plan configured — hide the option gracefully.

  const paypal = await loadScript({
    clientId,
    currency: 'USD',
    intent: 'subscription',
    vault: true,
    components: 'buttons',
  });
  if (!paypal?.Buttons) return;

  // Bail if the user navigated away while the SDK was loading.
  if (step.value !== 'checkout' || !paypalMountRef.value) return;

  paypalButtons = paypal.Buttons({
    style: { color: 'gold', shape: 'rect', label: 'subscribe', height: 44 },
    // custom_id carries the buyer's userId so /api/billing/paypal/webhook can
    // resolve + activate the subscription on its own when the onApprove →
    // /activate call below never lands (tab closed, network drop). Without it
    // the webhook has nothing to match on and a paid subscriber is never linked.
    createSubscription: (_data, actions) =>
      actions.subscription.create({
        plan_id: planId,
        custom_id: JSON.stringify({ userId: authStore.user?.id ?? null }),
        application_context: {
          shipping_preference: 'NO_SHIPPING',
          user_action: 'SUBSCRIBE_NOW',
          brand_name: 'Insiders of Dubai',
        },
      }),
    onApprove: async (data) => {
      if (!data.subscriptionID) return;
      paying.value = true;
      try {
        await $fetch('/api/billing/paypal/activate', {
          method: 'POST',
          headers: authHeaders(),
          body: { subscriptionID: data.subscriptionID },
        });
        await pollBillingUntilActive();
        // Attribute the subscription sale to the VSL the buyer watched (Vidalytics).
        useVidalyticsConversion().trackSale();
        trackPurchase(data.subscriptionID, 'paypal');
        step.value = 'success';
      } catch (error) {
        useErrorHandler().catchErrors(error);
        paying.value = false;
      }
    },
    onError: () => {
      payError.value = { key: 'billing.pricingModal.checkout.paypalError' };
    },
  });
  await paypalButtons.render(paypalMountRef.value);
  paypalAvailable.value = true;

  // PayPal sizes its buttons to the container width measured at render time,
  // which can be narrower than the final width in the animating/scrolling modal
  // (the buttons then look left-aligned vs the full-width card button). PayPal's
  // buttons re-flow to their container on a window resize, so nudge it once the
  // layout has settled at full width.
  await nextTick();
  if (import.meta.client) window.dispatchEvent(new Event('resize'));
};

const startCheckout = async () => {
  if (!selectedPlan.value) return;
  checkoutErrorKey.value = null;
  payError.value = null;
  paying.value = false;
  checkoutLoading.value = true;
  try {
    const res = await $fetch<{
      success: boolean;
      data: {
        subscriptionId?: string;
        clientSecret?: string | null;
        alreadySubscribed?: boolean;
        amountUsd?: number | null;
      };
    }>('/api/billing/subscribe', {
      method: 'POST',
      headers: authHeaders(),
      body: { plan: selectedPlan.value.plan, interval: selectedPlan.value.interval },
    });

    checkoutLoading.value = false;
    checkoutAmountUsd = res.data?.amountUsd ?? null;
    stripeSubscriptionId = res.data?.subscriptionId ?? null;
    if (res.data?.alreadySubscribed) {
      step.value = 'success';
      return;
    }
    trackInitiateCheckout(stripeSubscriptionId);
    const clientSecret = res.data?.clientSecret;
    if (!clientSecret) throw new Error('Missing client secret');

    // Reveal the payment container BEFORE mounting so Stripe/PayPal measure a
    // real width (mounting into a display:none node renders at 0px).
    await nextTick();
    await mountStripe(clientSecret);
    await mountPaypal();
  } catch (error) {
    useErrorHandler().catchErrors(error);
    checkoutErrorKey.value = 'billing.pricingModal.checkout.error';
    checkoutLoading.value = false;
  }
};

const pay = async () => {
  if (!stripe || !elements || paying.value) return;
  payError.value = null;
  paying.value = true;
  try {
    const result = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: { return_url: window.location.href },
    });
    if (result.error) {
      payError.value = result.error.message
        ? { text: result.error.message }
        : { key: 'billing.pricingModal.checkout.paymentFailed' };
      paying.value = false;
      return;
    }
    await pollBillingUntilActive();
    // Attribute the subscription sale to the VSL the buyer watched (Vidalytics).
    useVidalyticsConversion().trackSale();
    trackPurchase(stripeSubscriptionId, 'stripe');
    step.value = 'success';
  } catch (error) {
    useErrorHandler().catchErrors(error);
    paying.value = false;
  }
};

// Tear down Stripe Elements + PayPal buttons so re-entering checkout (or the
// user toggling back to plans) never double-mounts. The cached Stripe.js
// instance itself is safe to reuse; only the clientSecret-bound elements are
// disposed.
const teardownCheckout = () => {
  try {
    paymentElement?.unmount();
    paymentElement?.destroy();
  } catch {
    // element may already be gone with the removed DOM
  }
  paymentElement = null;
  elements = null;
  if (paypalButtons?.close) {
    void Promise.resolve(paypalButtons.close()).catch(() => {});
  }
  paypalButtons = null;
  if (paypalMountRef.value) paypalMountRef.value.innerHTML = '';
  paypalAvailable.value = false;
  paying.value = false;
  payError.value = null;
  checkoutErrorKey.value = null;
};

const backToPlans = () => {
  step.value = 'plans';
};

const goToDashboard = () => {
  close();
  if (route.path !== '/dashboard') navigateTo('/dashboard');
};

const goToTeamManagement = () => {
  close();
  navigateTo('/profile/team');
};

const storeNotice = computed(() =>
  storeManagedBy.value === 'app_store'
    ? t('billing.pricingModal.store.noticeAppStore')
    : t('billing.pricingModal.store.noticeGooglePlay'),
);
const storeManageCta = computed(() =>
  storeManagedBy.value === 'app_store'
    ? t('billing.pricingModal.store.manageAppStore')
    : t('billing.pricingModal.store.manageGooglePlay'),
);

// Billing state can land after the modal opened on a pre-selected plan.
watch(storeManagedBy, (by) => {
  if (by && step.value === 'checkout') step.value = 'plans';
});

const openStoreManage = () => {
  if (storeManageUrl.value) window.open(storeManageUrl.value, '_blank', 'noopener');
};

const manage = async () => {
  try {
    if (storeManagedBy.value) {
      openStoreManage();
      return;
    }
    if (provider.value === 'paypal') {
      const confirmed = window.confirm(t('billing.pricingModal.manageCancelConfirm'));
      if (!confirmed) return;
      await $fetch('/api/billing/paypal/cancel', { method: 'POST', headers: authHeaders() });
      showSnackBanner('success', t('billing.pricingModal.manageCancelSuccess'));
      qc.invalidateQueries({ queryKey: queries.billing.me.queryKey });
      return;
    }
    // Stripe (default): hand off to the customer portal.
    const res = await $fetch<{ success: boolean; data: { url: string } }>('/api/billing/portal', {
      method: 'POST',
      headers: authHeaders(),
    });
    window.location.href = res.data.url;
  } catch (error) {
    useErrorHandler().catchErrors(error);
  }
};

// Drive the payment lifecycle off the step machine so every entry/exit path
// (card, PayPal, back button, close) mounts/tears down exactly once.
watch(step, (val, prev) => {
  if (prev === 'checkout' && val !== 'checkout') teardownCheckout();
  if (val === 'checkout') startCheckout();
  if (val === 'success') qc.invalidateQueries({ queryKey: queries.billing.me.queryKey });
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="pricing-modal-root"
    >
      <!-- Overlay -->
      <div
        class="pm-overlay"
        @click="close"
      />

      <!-- Scroll wrapper (click-outside closes) -->
      <div
        class="pm-scroll-wrap"
        @click.self="close"
      >
        <div
          class="pm-modal"
          role="dialog"
          aria-modal="true"
          :aria-label="t('billing.pricingModal.dialogLabel')"
        >
          <!-- Close button -->
          <button
            class="pm-close"
            :aria-label="t('billing.pricingModal.closeAria')"
            @click="close"
          >
            &times;
          </button>

          <!-- ══════════════════════════════════════════════════════════
               STEP: PLANS
          ══════════════════════════════════════════════════════════ -->
          <template v-if="step === 'plans'">
            <!-- Header -->
            <div class="pm-head">
              <div class="pm-wordmark">
                <UILogo
                  variant="compact"
                  :size="28"
                  aria-label="Insiders of Dubai"
                />
              </div>
              <p class="pm-sub">
                {{ t('billing.pricingModal.subtitle') }}
              </p>
            </div>

            <!-- Plan billed by the App Store / Google Play: managed there -->
            <div
              v-if="storeManagedBy"
              class="pm-store-note"
              data-testid="pricing-store-managed"
            >
              <p>{{ storeNotice }}</p>
              <button
                v-if="storeManageUrl"
                type="button"
                class="pm-store-note__cta"
                @click="openStoreManage"
              >
                {{ storeManageCta }}
              </button>
            </div>

            <!-- Billing toggle -->
            <div class="pm-togglewrap">
              <div
                class="pm-toggle"
                role="group"
                :aria-label="t('billing.pricingModal.billingToggleAria')"
              >
                <span
                  class="pm-knob"
                  :style="knobStyle"
                />
                <button
                  ref="annualBtnRef"
                  type="button"
                  :class="['pm-toggle-btn', isAnnual && 'active']"
                  :aria-pressed="isAnnual"
                  data-billing="annual"
                  @click="billingInterval = 'ANNUAL'"
                >
                  {{ t('billing.pricingModal.checkout.annual') }}
                </button>
                <button
                  ref="monthlyBtnRef"
                  type="button"
                  :class="['pm-toggle-btn', !isAnnual && 'active']"
                  :aria-pressed="!isAnnual"
                  data-billing="monthly"
                  @click="billingInterval = 'MONTHLY'"
                >
                  {{ t('billing.pricingModal.checkout.monthly') }}
                </button>
              </div>
              <span :class="['pm-save', !isAnnual && 'hide']">{{ saveBadgeText }}</span>
            </div>

            <!-- ── Card grid ── -->
            <div
              ref="cardGridRef"
              class="pm-grid"
              @scroll="onGridScroll"
            >
              <!-- MEMBER card -->
              <div :class="['pm-card', isMemberCurrent && 'pm-card--current']">
                <div class="pm-tier">
                  {{ t('billing.pricingModal.member.tier') }}
                  <span
                    v-if="isMemberCurrent"
                    class="pm-current-tag"
                    >{{ t('billing.pricingModal.currentPlanTag') }}</span
                  >
                </div>
                <div class="pm-pricerow">
                  <span class="pm-price">{{ memberPrice }}</span>
                  <span class="pm-per">{{ perPeriodText }}</span>
                </div>
                <div class="pm-billed">{{ memberBilledText }}</div>
                <div class="pm-tagline">{{ t('billing.pricingModal.member.tagline') }}</div>
                <button
                  :class="['pm-cta', isMemberCurrent && 'pm-cta--current']"
                  :disabled="isMemberCurrent"
                  @click="isMemberCurrent ? manage() : selectPlan('MEMBER', billingInterval)"
                >
                  {{ isMemberCurrent ? t('billing.pricingModal.manage') : t('billing.pricingModal.member.cta') }}
                </button>
                <ul class="pm-feats">
                  <li class="pm-feat">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>{{ t('billing.pricingModal.member.feat1') }}</span>
                  </li>
                  <li class="pm-feat">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>{{ t('billing.pricingModal.member.feat2') }}</span>
                  </li>
                  <li class="pm-feat pm-feat--lit">
                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M13 2L4.5 12.5a.6.6 0 0 0 .5 1h6l-1 8.5L18.5 11.5a.6.6 0 0 0-.5-1h-6z" />
                    </svg>
                    <span>{{ t('billing.pricingModal.member.feat3') }}</span>
                  </li>
                  <li class="pm-feat pm-feat--lit">
                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M13 2L4.5 12.5a.6.6 0 0 0 .5 1h6l-1 8.5L18.5 11.5a.6.6 0 0 0-.5-1h-6z" />
                    </svg>
                    <span>{{ t('billing.pricingModal.member.feat4') }}</span>
                  </li>
                  <li :class="['pm-feat', !isAnnual && 'pm-feat--dim']">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span v-if="isAnnual">{{ t('billing.pricingModal.member.feat5') }}</span>
                    <span v-else>{{ t('billing.pricingModal.masterclassAnnualOnly') }}</span>
                  </li>
                  <li class="pm-feat">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>{{ t('billing.pricingModal.member.feat6') }}</span>
                  </li>
                  <li class="pm-feat">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>{{ t('billing.pricingModal.member.feat7') }}</span>
                  </li>
                  <li class="pm-feat">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>{{ t('billing.pricingModal.member.feat8') }}</span>
                  </li>
                </ul>
              </div>

              <!-- PRO card (Most popular) -->
              <div class="pm-card pm-card--pop">
                <div class="pm-badge">{{ t('billing.pricingModal.mostPopular') }}</div>
                <div class="pm-tier">
                  {{ t('billing.pricingModal.pro.tier') }}
                  <span
                    v-if="isProCurrent"
                    class="pm-current-tag"
                    >{{ t('billing.pricingModal.currentPlanTag') }}</span
                  >
                </div>
                <div class="pm-pricerow">
                  <span class="pm-price">{{ proPrice }}</span>
                  <span class="pm-per">{{ perPeriodText }}</span>
                </div>
                <div class="pm-billed">{{ proBilledText }}</div>
                <div class="pm-tagline">{{ t('billing.pricingModal.pro.tagline') }}</div>
                <button
                  :class="['pm-cta pm-cta--solid', isProCurrent && 'pm-cta--current']"
                  :disabled="isProCurrent"
                  @click="isProCurrent ? manage() : selectPlan('PRO', billingInterval)"
                >
                  {{ isProCurrent ? t('billing.pricingModal.manage') : t('billing.pricingModal.pro.cta') }}
                </button>
                <ul class="pm-feats">
                  <li class="pm-feat">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>{{ t('billing.pricingModal.pro.feat1') }}</span>
                  </li>
                  <li class="pm-feat pm-feat--lit">
                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M13 2L4.5 12.5a.6.6 0 0 0 .5 1h6l-1 8.5L18.5 11.5a.6.6 0 0 0-.5-1h-6z" />
                    </svg>
                    <span>{{ t('billing.pricingModal.pro.feat2') }}</span>
                  </li>
                  <li class="pm-feat pm-feat--lit">
                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="9"
                      />
                      <path d="M12 7v5l3 2" />
                    </svg>
                    <span>{{ t('billing.pricingModal.pro.feat3') }}</span>
                  </li>
                  <li class="pm-feat">
                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <circle
                        cx="9"
                        cy="8"
                        r="3.4"
                      />
                      <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
                      <path d="M18 6.2l.9 1.9 2 .3-1.5 1.4.4 2-1.8-1-1.8 1 .4-2L15 8.4l2-.3z" />
                    </svg>
                    <span>{{ t('billing.pricingModal.pro.feat4') }}</span>
                  </li>
                  <li class="pm-feat">
                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M21 12a8 8 0 0 1-11.6 7.1L4 21l1.9-5.4A8 8 0 1 1 21 12z" />
                      <path d="M12 8.2l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9z" />
                    </svg>
                    <span>{{ t('billing.pricingModal.pro.feat5') }}<sup class="pm-sup">1</sup></span>
                  </li>
                  <li class="pm-feat">
                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M15 4V2M15 10V8M11 6H9M21 6h-2" />
                      <path d="M4 20l11-11 1.5 1.5L5.5 21.5z" />
                      <path d="M14 7l3 3" />
                    </svg>
                    <span>{{ t('billing.pricingModal.pro.feat6') }}<sup class="pm-sup">1</sup></span>
                  </li>
                  <li :class="['pm-feat', !isAnnual && 'pm-feat--dim']">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span v-if="isAnnual">{{ t('billing.pricingModal.pro.feat7') }}</span>
                    <span v-else>{{ t('billing.pricingModal.masterclassAnnualOnly') }}</span>
                  </li>
                </ul>
              </div>

              <!-- TEAM card -->
              <div :class="['pm-card', isTeamCurrent && 'pm-card--current']">
                <div class="pm-tier">
                  {{ t('billing.pricingModal.team.tier') }}
                  <span
                    v-if="isTeamCurrent"
                    class="pm-current-tag"
                    >{{ t('billing.pricingModal.currentPlanTag') }}</span
                  >
                </div>
                <div class="pm-pricerow">
                  <span class="pm-price">{{ formatCurrency(TEAM_ANNUAL_TOTAL, 'USD') }}</span>
                  <span class="pm-per">{{ t('billing.pricingModal.perYear') }}</span>
                </div>
                <div class="pm-billed">{{ teamBilledText }}</div>
                <div class="pm-tagline">{{ t('billing.pricingModal.team.tagline') }}</div>
                <button
                  :class="['pm-cta', isTeamCurrent && !isTeamOwner && 'pm-cta--current']"
                  :disabled="isTeamCurrent && !isTeamOwner"
                  @click="isTeamOwner ? goToTeamManagement() : isTeamCurrent ? manage() : selectPlan('TEAM', 'ANNUAL')"
                >
                  {{
                    isTeamOwner
                      ? t('billing.pricingModal.team.ctaOwner')
                      : isTeamCurrent
                        ? t('billing.pricingModal.manage')
                        : t('billing.pricingModal.team.cta')
                  }}
                </button>
                <ul class="pm-feats">
                  <li class="pm-feat">
                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <circle
                        cx="8"
                        cy="9"
                        r="3"
                      />
                      <path d="M2.5 19a5.5 5.5 0 0 1 11 0" />
                      <circle
                        cx="17"
                        cy="8"
                        r="2.6"
                      />
                      <path d="M15 19a5 5 0 0 1 6.5-4.8" />
                    </svg>
                    <span>{{ t('billing.pricingModal.team.feat1') }}</span>
                  </li>
                  <li class="pm-feat">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>{{ t('billing.pricingModal.team.feat2') }}</span>
                  </li>
                  <li class="pm-feat">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>{{ t('billing.pricingModal.team.feat3') }}</span>
                  </li>
                  <li
                    v-if="!isAnnual"
                    class="pm-feat pm-feat--dim"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="9"
                      />
                      <path d="M12 8v4M12 16h.01" />
                    </svg>
                    <span>{{ t('billing.pricingModal.team.annualOnly') }}</span>
                  </li>
                </ul>
              </div>
            </div>
            <!-- /pm-grid -->

            <!-- Mobile swipe hint (hidden on desktop via SCSS) -->
            <div class="pm-swipe-hint">
              <span>{{ t('billing.pricingModal.swipeHint') }}</span>
              <span class="pm-dots">
                <i :class="{ on: activeDot === 0 }" />
                <i :class="{ on: activeDot === 1 }" />
                <i :class="{ on: activeDot === 2 }" />
              </span>
            </div>

            <!-- Notes -->
            <div class="pm-notes">
              <p>{{ t('billing.pricingModal.notes.closingManager') }}</p>
              <p><sup class="pm-sup">1</sup> {{ t('billing.pricingModal.notes.fairUseApplies') }}</p>
            </div>

            <!-- Proof stats -->
            <div class="pm-proof">
              <div class="pm-proof-item">
                <div class="pm-proof-ic">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M3 17l6-6 4 4 8-8" />
                    <path d="M17 7h4v4" />
                  </svg>
                </div>
                <div class="pm-proof-n">{{ t('billing.pricingModal.proof.salesVolume') }}</div>
                <div class="pm-proof-l">{{ t('billing.pricingModal.proof.salesVolumeSub') }}</div>
              </div>
              <div class="pm-proof-item">
                <div class="pm-proof-ic">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect
                      x="4"
                      y="3"
                      width="16"
                      height="18"
                      rx="1.5"
                    />
                    <path d="M9 21v-4h6v4" />
                    <path d="M8 7h.01M12 7h.01M16 7h.01M8 11h.01M12 11h.01M16 11h.01" />
                  </svg>
                </div>
                <div class="pm-proof-n">{{ t('billing.pricingModal.proof.relationships') }}</div>
                <div class="pm-proof-l">{{ t('billing.pricingModal.proof.relationshipsSub') }}</div>
              </div>
              <div class="pm-proof-item">
                <div class="pm-proof-ic">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle
                      cx="12"
                      cy="10"
                      r="3"
                    />
                  </svg>
                </div>
                <div class="pm-proof-n">{{ t('billing.pricingModal.proof.projects') }}</div>
                <div class="pm-proof-l">{{ t('billing.pricingModal.proof.projectsSub') }}</div>
              </div>
              <div class="pm-proof-item">
                <div class="pm-proof-ic">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="9"
                    />
                    <path d="M12 7v5l3 2" />
                  </svg>
                </div>
                <div class="pm-proof-n">{{ t('billing.pricingModal.proof.payout') }}</div>
                <div class="pm-proof-l">{{ t('billing.pricingModal.proof.payoutSub') }}</div>
              </div>
            </div>
            <p class="pm-proof-note">
              {{ t('billing.pricingModal.proofNote') }}
            </p>

            <!-- Compare table -->
            <div class="pm-compare">
              <h3 class="pm-section-title">{{ t('billing.pricingModal.compareTitle') }}</h3>
              <table class="pm-table">
                <thead>
                  <tr>
                    <th />
                    <th>
                      {{ t('billing.pricingModal.member.tier') }}
                      <small>{{ memberComparePrice }}</small>
                    </th>
                    <th class="pm-procol pm-th-pro">
                      {{ t('billing.pricingModal.pro.tier') }}
                      <small>{{ proComparePrice }}</small>
                    </th>
                    <th>
                      {{ t('billing.pricingModal.team.tier') }}
                      <small>{{
                        t('billing.pricingModal.priceYear', { amount: formatCurrency(TEAM_ANNUAL_TOTAL, 'USD') })
                      }}</small>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, rowIdx) in TABLE_ROWS"
                    :key="rowIdx"
                  >
                    <td class="pm-td-fn">{{ row[0] }}</td>
                    <td>
                      <template v-if="row[1] === 'ck'">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2.4"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          class="pm-table-ck"
                        >
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </template>
                      <span
                        v-else-if="row[1] === 'dash'"
                        class="pm-dash"
                        >&mdash;</span
                      >
                      <span
                        v-else
                        class="pm-note"
                        >{{ row[1] }}</span
                      >
                    </td>
                    <td class="pm-procol">
                      <template v-if="row[2] === 'ck'">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2.4"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          class="pm-table-ck"
                        >
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </template>
                      <span
                        v-else-if="row[2] === 'dash'"
                        class="pm-dash"
                        >&mdash;</span
                      >
                      <span
                        v-else
                        class="pm-note"
                        >{{ row[2] }}</span
                      >
                    </td>
                    <td>
                      <template v-if="row[3] === 'ck'">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2.4"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          class="pm-table-ck"
                        >
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </template>
                      <span
                        v-else-if="row[3] === 'dash'"
                        class="pm-dash"
                        >&mdash;</span
                      >
                      <span
                        v-else
                        class="pm-note"
                        >{{ row[3] }}</span
                      >
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- FAQ -->
            <div class="pm-faq">
              <h3 class="pm-section-title">{{ t('billing.pricingModal.faqTitle') }}</h3>
              <details
                v-for="([q, a], faqIdx) in FAQ_ITEMS"
                :key="faqIdx"
                class="pm-faq-item"
              >
                <summary class="pm-faq-q">{{ q }}</summary>
                <p class="pm-faq-a">{{ a }}</p>
              </details>
            </div>

            <!-- Closing CTA -->
            <div class="pm-close-cta">
              <h4 class="pm-close-cta-h">{{ t('billing.pricingModal.closing.title') }}<span class="pm-dot">.</span></h4>
              <p class="pm-close-cta-p">{{ t('billing.pricingModal.closing.subtitle') }}</p>
              <button
                class="pm-close-cta-btn"
                @click="isProCurrent ? manage() : selectPlan('PRO', billingInterval)"
              >
                {{
                  isProCurrent
                    ? t('billing.pricingModal.closing.ctaManage', { tier: t('billing.plans.pro') })
                    : closingCtaText
                }}
              </button>
              <div class="pm-close-cta-tiny">{{ t('billing.pricingModal.closing.tiny') }}</div>
            </div>
          </template>
          <!-- /step: plans -->

          <!-- ══════════════════════════════════════════════════════════
               STEP: CHECKOUT (placeholder — wired in next task)
          ══════════════════════════════════════════════════════════ -->
          <template v-else-if="step === 'checkout'">
            <div class="pm-checkout">
              <h2 class="pm-checkout-title">
                {{ checkoutSummary?.name ?? '' }}
                <span class="pm-checkout-interval">{{ checkoutSummary?.interval ?? '' }}</span>
              </h2>

              <!-- Order summary -->
              <div class="pm-checkout-summary">
                <div class="pm-checkout-summary-label">{{ t('billing.pricingModal.checkout.orderSummary') }}</div>
                <div class="pm-checkout-summary-plan">{{ checkoutSummary?.name }}</div>
                <div class="pm-checkout-summary-price">{{ checkoutSummary?.price }}</div>
                <ul
                  v-if="checkoutSummary?.features?.length"
                  class="pm-checkout-summary-features"
                >
                  <li
                    v-for="f in checkoutSummary.features"
                    :key="f"
                  >
                    {{ f }}
                  </li>
                </ul>
              </div>

              <!-- Loading while we mint the subscription + load Stripe.js -->
              <div
                v-if="checkoutLoading"
                class="pm-checkout-loading"
              >
                <span class="pm-spinner" />
              </div>

              <!-- Init error (subscribe failed) -->
              <div
                v-else-if="checkoutErrorKey"
                class="pm-checkout-error"
              >
                <p>{{ t(checkoutErrorKey) }}</p>
                <button
                  class="pm-retry-btn"
                  @click="startCheckout"
                >
                  {{ t('billing.pricingModal.checkout.tryAgain') }}
                </button>
              </div>

              <!-- Payment surface — kept in the DOM (v-show) so mount refs persist -->
              <div v-show="!checkoutLoading && !checkoutErrorKey">
                <!-- Stripe Elements mount -->
                <div
                  ref="paymentMountRef"
                  class="pm-payment-mount"
                />

                <p
                  v-if="payErrorText"
                  class="pm-pay-error"
                >
                  {{ payErrorText }}
                </p>

                <button
                  class="pm-pay-btn"
                  :disabled="paying"
                  @click="pay"
                >
                  <span v-if="!paying">{{ t('billing.pricingModal.checkout.payCta') }}</span>
                  <span v-else>{{ t('billing.pricingModal.checkout.processing') }}</span>
                </button>

                <p class="pm-pay-secure">{{ t('billing.pricingModal.checkout.secureNote') }}</p>

                <!-- PayPal (secondary) — divider only shows once buttons render -->
                <div
                  v-show="paypalAvailable"
                  class="pm-or"
                >
                  <span class="pm-or-line" />
                  <span class="pm-or-txt">{{ t('billing.pricingModal.checkout.or') }}</span>
                  <span class="pm-or-line" />
                </div>
                <div
                  ref="paypalMountRef"
                  class="pm-paypal-mount"
                />
              </div>

              <!-- Back to plans -->
              <button
                class="pm-back-btn"
                :disabled="paying"
                @click="backToPlans"
              >
                <svg
                  class="rtl:-scale-x-100"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                {{ t('billing.pricingModal.checkout.backToPlans') }}
              </button>
            </div>
          </template>
          <!-- /step: checkout -->

          <!-- ══════════════════════════════════════════════════════════
               STEP: SUCCESS (placeholder — wired in next task)
          ══════════════════════════════════════════════════════════ -->
          <template v-else-if="step === 'success'">
            <div class="pm-success">
              <div class="pm-success-icon">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h2 class="pm-success-title">{{ t('billing.pricingModal.success.title') }}</h2>
              <p class="pm-success-sub">{{ t('billing.pricingModal.success.subtitle') }}</p>
              <button
                class="pm-success-btn"
                @click="goToDashboard"
              >
                {{ t('billing.pricingModal.success.goToDashboard') }}
              </button>
            </div>
          </template>
          <!-- /step: success -->
        </div>
        <!-- /pm-modal -->
      </div>
      <!-- /pm-scroll-wrap -->
    </div>
    <!-- /pricing-modal-root -->
  </Teleport>
</template>

<style scoped lang="scss">
/* ── Custom properties (design token overrides for this modal) ───────────── */
.pricing-modal-root {
  // CSS vars mapped from design
  --pm-bg: #fffdf8;
  --pm-gold: #ffb648;
  --pm-gold-wash: #fff6e7;
  --pm-gold-line: rgba(212, 160, 50, 0.35);
  --pm-gold-txt: #8b6000;
  --pm-on-gold: #0d0f15;
  --pm-tx: #0d0f15;
  --pm-tx2: rgba(13, 15, 21, 0.68);
  --pm-mut: rgba(13, 15, 21, 0.42);
  --pm-bd: rgba(13, 15, 21, 0.09);
  --pm-bd2: rgba(13, 15, 21, 0.16);
  --pm-cd: #ffffff;
  --pm-cd-pop: #fff6e7;
  --pm-pro-tint: rgba(255, 182, 72, 0.07);

  position: fixed;
  inset: 0;
  z-index: 1000;
  font-family: 'Manrope', sans-serif;
  -webkit-font-smoothing: antialiased;
}

/* ── Overlay ─────────────────────────────────────────────────────────────── */
.pm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(13, 15, 21, 0.55);
  backdrop-filter: blur(3px);
}

/* ── Scroll container ────────────────────────────────────────────────────── */
.pm-scroll-wrap {
  position: fixed;
  inset: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 52px 16px 52px;

  @media (max-width: 743px) {
    padding: 72px 0 0;
    align-items: flex-end;
  }
}

/* ── Modal card ──────────────────────────────────────────────────────────── */
.pm-modal {
  position: relative;
  width: 1150px;
  max-width: 100%;
  background: var(--pm-bg);
  color: var(--pm-tx);
  border-radius: 26px;
  padding: 64px 64px 72px;
  box-shadow: 0 60px 140px -40px rgba(0, 0, 0, 0.55);
  flex-shrink: 0;

  @media (max-width: 743px) {
    border-radius: 22px 22px 0 0;
    padding: 44px 20px 56px;
    width: 100%;
    // Bottom-sheet: fit the viewport and scroll the card internally. Without a
    // height cap, a card taller than the screen gets its top (logo + billing
    // toggle) clipped above the fold by the flex-end alignment and can't be
    // scrolled to — the reported "only see the bottom, no slider" bug.
    max-height: calc(100vh - 72px);
    max-height: calc(100dvh - 72px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
}

/* ── Close button ────────────────────────────────────────────────────────── */
.pm-close {
  position: absolute;
  top: 22px;
  inset-inline-end: 22px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid var(--pm-bd);
  background: transparent;
  color: var(--pm-mut);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;

  &:hover {
    background: var(--pm-bd);
    color: var(--pm-tx);
  }

  @media (max-width: 743px) {
    top: 16px;
    inset-inline-end: 16px;
    width: 36px;
    height: 36px;
  }
}

/* ── Header ──────────────────────────────────────────────────────────────── */
.pm-head {
  text-align: center;
  margin-bottom: 32px;
}

.pm-wordmark {
  font-family: 'Fraunces', serif;
  font-weight: 800;
  font-size: 25px;
  letter-spacing: 0.01em;
  margin-bottom: 18px;

  :deep(i) {
    font-style: normal;
    color: var(--pm-gold);
  }

  i {
    font-style: normal;
    color: var(--pm-gold);
  }

  @media (max-width: 743px) {
    font-size: 22px;
  }
}

.pm-store-note {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px 14px;
  max-width: 560px;
  margin: 14px auto 0;
  padding: 12px 16px;
  border: 1px solid var(--pm-bd2);
  border-radius: 14px;
  background: var(--pm-cd);
  color: var(--pm-tx);
  font-size: 14px;
  text-align: center;

  p {
    margin: 0;
    min-width: 0;
    overflow-wrap: anywhere;
  }
}

.pm-store-note__cta {
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.pm-sub {
  font-size: 15.5px;
  color: var(--pm-tx2);
  max-width: 560px;
  margin: 0 auto;

  @media (max-width: 743px) {
    font-size: 15px;
  }
}

/* ── Billing toggle ──────────────────────────────────────────────────────── */
.pm-togglewrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 11px;
  margin-bottom: 42px;
}

.pm-toggle {
  display: inline-flex;
  background: #f2f2f3;
  border: 1px solid var(--pm-bd);
  border-radius: 999px;
  padding: 4px;
  position: relative;
}

.pm-toggle-btn {
  position: relative;
  z-index: 1;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: 'Manrope', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--pm-mut);
  padding: 8px 20px;
  border-radius: 999px;
  transition: color 0.2s;

  &.active {
    color: var(--pm-tx);
  }
}

// Physical `left` on purpose: updateKnob() translates by the button's physical
// offsetLeft, which keeps the knob correct in RTL too.
.pm-knob {
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: 4px;
  background: #ffffff;
  border-radius: 999px;
  box-shadow: 0 1px 4px rgba(13, 15, 21, 0.14);
  transition:
    transform 0.26s cubic-bezier(0.4, 0, 0.2, 1),
    width 0.26s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
}

.pm-save {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--pm-gold-txt);
  background: var(--pm-gold-wash);
  border: 1px solid var(--pm-gold-line);
  border-radius: 999px;
  padding: 4px 12px;
  transition:
    opacity 0.2s,
    transform 0.2s;

  &.hide {
    opacity: 0;
    transform: translateY(-3px);
    pointer-events: none;
  }
}

/* ── Plans grid ──────────────────────────────────────────────────────────── */
.pm-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
  align-items: stretch;

  @media (max-width: 743px) {
    display: flex;
    grid-template-columns: none;
    gap: 12px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    margin: 0 -20px;
    padding: 14px 20px 6px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}

/* ── Pricing card ────────────────────────────────────────────────────────── */
.pm-card {
  position: relative;
  background: var(--pm-cd);
  border: 1px solid var(--pm-bd);
  border-radius: 18px;
  padding: 30px 26px;
  display: flex;
  flex-direction: column;

  @media (max-width: 743px) {
    flex: 0 0 82%;
    scroll-snap-align: center;
  }

  // Popular (Pro) variant
  &.pm-card--pop {
    background: var(--pm-cd-pop);
    border: 2px solid var(--pm-gold);
    padding: 29px 25px;

    .pm-tagline,
    .pm-billed,
    .pm-per {
      color: var(--pm-tx2);
    }

    .pm-feat {
      color: var(--pm-tx2);
    }
  }

  // Current plan highlight
  &.pm-card--current {
    border-color: var(--pm-bd2);
  }
}

.pm-badge {
  position: absolute;
  top: -13px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--pm-gold);
  color: var(--pm-on-gold);
  font-size: 11.5px;
  font-weight: 700;
  padding: 5px 14px;
  border-radius: 999px;
  white-space: nowrap;
}

.pm-tier {
  font-family: 'Fraunces', serif;
  font-weight: 700;
  font-size: 15.5px;
  margin-bottom: 16px;
}

.pm-current-tag {
  display: inline-block;
  vertical-align: 2px;
  margin-inline-start: 8px;
  font-family: 'Manrope', sans-serif;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--pm-mut);
  border: 1px solid var(--pm-bd2);
  border-radius: 999px;
  padding: 3px 9px;
}

.pm-pricerow {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.pm-price {
  font-family: 'Fraunces', serif;
  font-weight: 700;
  font-size: 42px;
  letter-spacing: -0.03em;
  line-height: 1;

  @media (max-width: 743px) {
    font-size: 38px;
  }
}

.pm-per {
  font-size: 14px;
  color: var(--pm-mut);
}

.pm-billed {
  font-size: 13px;
  color: var(--pm-mut);
  margin-top: 9px;
  min-height: 18px;
}

.pm-tagline {
  font-size: 14px;
  color: var(--pm-tx2);
  margin-top: 11px;
}

/* ── Card CTA button ─────────────────────────────────────────────────────── */
.pm-cta {
  display: block;
  width: 100%;
  text-align: center;
  font-size: 14.5px;
  font-weight: 700;
  padding: 13px 18px;
  border-radius: 11px;
  cursor: pointer;
  border: 1px solid var(--pm-bd2);
  margin: 22px 0 26px;
  background: transparent;
  color: inherit;
  font-family: 'Manrope', sans-serif;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: var(--pm-bd);
  }

  // Gold / solid variant (Pro)
  &.pm-cta--solid {
    background: var(--pm-gold);
    color: var(--pm-on-gold);
    border-color: var(--pm-gold);

    &:hover:not(:disabled) {
      background: #eda436;
      border-color: #eda436;
    }
  }

  // Current plan state
  &.pm-cta--current {
    cursor: default;
    color: var(--pm-mut);
    background: transparent;
    border-style: dashed;

    &:hover {
      background: transparent;
    }
  }
}

/* ── Feature list ────────────────────────────────────────────────────────── */
.pm-feats {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.pm-feat {
  display: flex;
  gap: 11px;
  align-items: flex-start;
  font-size: 14px;
  line-height: 1.45;
  color: var(--pm-tx2);

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: var(--pm-gold);
  }

  // Highlighted (bold) feature
  &.pm-feat--lit {
    color: var(--pm-tx);
    font-weight: 600;
  }

  // Dimmed feature (monthly plan restriction)
  &.pm-feat--dim {
    color: var(--pm-mut);

    svg {
      color: var(--pm-mut);
    }
  }
}

.pm-sup {
  color: var(--pm-gold-txt);
  font-size: 0.72em;
  vertical-align: super;
  margin-inline-start: 1px;
  text-decoration: none;
}

/* ── Mobile swipe hint ───────────────────────────────────────────────────── */
.pm-swipe-hint {
  display: none;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 10px;
  font-size: 12px;
  color: var(--pm-mut);

  @media (max-width: 743px) {
    display: flex;
  }
}

.pm-dots {
  display: flex;
  gap: 6px;

  i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--pm-bd);
    transition:
      background 0.2s,
      width 0.2s;
    display: block;

    &.on {
      background: var(--pm-gold);
      width: 16px;
      border-radius: 3px;
    }
  }
}

/* ── Notes beneath cards ─────────────────────────────────────────────────── */
.pm-notes {
  text-align: center;
  margin-top: 36px;
  display: flex;
  flex-direction: column;
  gap: 7px;

  p {
    margin: 0;
    font-size: 12.5px;
    color: var(--pm-mut);

    @media (max-width: 743px) {
      font-size: 11.5px;
    }
  }
}

/* ── Proof stats row ─────────────────────────────────────────────────────── */
.pm-proof {
  margin-top: 40px;
  display: flex;
  justify-content: center;
  border-top: 1px solid var(--pm-bd);
  border-bottom: 1px solid var(--pm-bd);
  padding: 34px 0;

  @media (max-width: 743px) {
    flex-direction: column;
    gap: 18px;
    padding: 20px 0;
  }
}

.pm-proof-item {
  flex: 1;
  text-align: center;
  padding: 0 26px;

  & + & {
    border-inline-start: 1px solid var(--pm-bd);

    @media (max-width: 743px) {
      border-inline-start: none;
      border-top: 1px solid var(--pm-bd);
      padding-top: 18px;
    }
  }
}

.pm-proof-ic {
  color: var(--pm-gold);
  margin-bottom: 12px;
}

.pm-proof-n {
  font-family: 'Fraunces', serif;
  font-weight: 600;
  font-size: 18px;
  letter-spacing: -0.01em;
  color: var(--pm-tx);
}

.pm-proof-l {
  font-size: 12.5px;
  color: var(--pm-mut);
  margin-top: 6px;
}

.pm-proof-note {
  text-align: center;
  font-size: 13px;
  color: var(--pm-mut);
  max-width: 62ch;
  margin: 20px auto 0;
  line-height: 1.55;
}

/* ── Section title (compare / FAQ) ──────────────────────────────────────── */
.pm-section-title {
  font-family: 'Fraunces', serif;
  font-weight: 700;
  font-size: 21px;
  text-align: center;
  margin: 0 0 24px;

  @media (max-width: 743px) {
    font-size: 19px;
  }
}

/* ── Compare table ───────────────────────────────────────────────────────── */
.pm-compare {
  margin-top: 56px;
}

.pm-table {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 12px 16px;

    @media (max-width: 743px) {
      padding: 9px 6px;
    }
  }

  thead th {
    border-bottom: 1px solid var(--pm-bd2);
    font-family: 'Fraunces', serif;
    font-weight: 700;
    font-size: 14px;
    text-align: center;

    @media (max-width: 743px) {
      font-size: 12px;
    }

    &:first-child {
      width: 32%;
      text-align: start;
    }

    small {
      display: block;
      font-family: 'Manrope', sans-serif;
      font-size: 11.5px;
      font-weight: 500;
      color: var(--pm-mut);
      margin-top: 2px;

      @media (max-width: 743px) {
        font-size: 10px;
      }
    }
  }

  // Pro column header color
  .pm-th-pro {
    color: var(--pm-gold-txt);
  }

  tbody td {
    border-bottom: 1px solid var(--pm-bd);
    font-size: 13px;
    color: var(--pm-tx2);
    text-align: center;

    @media (max-width: 743px) {
      font-size: 11.5px;
    }
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  .pm-td-fn {
    text-align: start;
    color: var(--pm-tx);
    font-weight: 500;

    @media (max-width: 743px) {
      font-size: 12px;
    }
  }

  // Pro column tint
  .pm-procol {
    background: var(--pm-pro-tint);
  }
}

.pm-table-ck {
  color: var(--pm-gold);
  vertical-align: middle;
  display: block;
  margin: 0 auto;

  @media (max-width: 743px) {
    width: 13px;
    height: 13px;
  }
}

.pm-dash {
  color: var(--pm-mut);
}

.pm-note {
  font-size: 12px;
  color: var(--pm-mut);

  @media (max-width: 743px) {
    font-size: 10.5px;
  }
}

/* ── FAQ ─────────────────────────────────────────────────────────────────── */
.pm-faq {
  margin-top: 56px;
  max-width: 720px;
  margin-inline-start: auto;
  margin-inline-end: auto;
}

.pm-faq-item {
  border-bottom: 1px solid var(--pm-bd);

  &:first-of-type {
    border-top: 1px solid var(--pm-bd);
  }
}

.pm-faq-q {
  cursor: pointer;
  list-style: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 4px;
  font-size: 14.5px;
  font-weight: 600;
  color: var(--pm-tx);

  &::-webkit-details-marker {
    display: none;
  }

  &::after {
    content: '+';
    font-size: 18px;
    font-weight: 500;
    color: var(--pm-mut);
    flex-shrink: 0;
  }

  details[open] &::after {
    content: '\2013';
  }
}

.pm-faq-a {
  margin: 0;
  padding: 0 4px 16px;
  font-size: 13.5px;
  line-height: 1.55;
  color: var(--pm-tx2);
}

/* ── Closing CTA section ─────────────────────────────────────────────────── */
.pm-close-cta {
  margin-top: 60px;
  text-align: center;
  border-top: 1px solid var(--pm-bd);
  padding-top: 48px;
}

.pm-close-cta-h {
  font-family: 'Fraunces', serif;
  font-weight: 600;
  font-size: 28px;
  letter-spacing: -0.015em;
  margin: 0 0 10px;

  @media (max-width: 743px) {
    font-size: 24px;
  }
}

.pm-dot {
  color: var(--pm-gold);
}

.pm-close-cta-p {
  margin: 0 0 26px;
  font-size: 15px;
  color: var(--pm-tx2);
}

.pm-close-cta-btn {
  display: inline-block;
  background: var(--pm-gold);
  color: var(--pm-on-gold);
  font-size: 16px;
  font-weight: 700;
  padding: 16px 42px;
  border-radius: 13px;
  border: none;
  cursor: pointer;
  font-family: 'Manrope', sans-serif;
  transition: background 0.15s;

  &:hover {
    background: #eda436;
  }

  @media (max-width: 743px) {
    display: block;
    width: 100%;
    padding: 16px 20px;
  }
}

.pm-close-cta-tiny {
  margin-top: 14px;
  font-size: 12.5px;
  color: var(--pm-mut);

  @media (max-width: 743px) {
    font-size: 11.5px;
  }
}

/* ── Checkout step (placeholder) ─────────────────────────────────────────── */
.pm-checkout {
  padding: 8px 0;
}

.pm-checkout-title {
  font-family: 'Fraunces', serif;
  font-weight: 600;
  font-size: 26px;
  color: var(--pm-tx);
  margin: 0 0 24px;
}

.pm-checkout-interval {
  display: inline-block;
  margin-inline-start: 10px;
  font-family: 'Manrope', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: var(--pm-mut);
  vertical-align: middle;
}

.pm-checkout-summary {
  padding: 20px;
  border-radius: 14px;
  border: 1px solid var(--pm-bd);
  background: #ffffff;
  margin-bottom: 28px;
}

.pm-checkout-summary-label {
  font-size: 12px;
  color: var(--pm-mut);
  margin-bottom: 6px;
}

.pm-checkout-summary-plan {
  font-size: 18px;
  font-weight: 600;
  color: var(--pm-tx);
}

.pm-checkout-summary-price {
  font-size: 14px;
  color: var(--pm-mut);
  margin-top: 4px;
}

.pm-checkout-summary-features {
  list-style: none;
  margin: 14px 0 0;
  padding: 14px 0 0;
  border-top: 1px solid var(--pm-bd);
  display: grid;
  gap: 8px;

  li {
    position: relative;
    padding-inline-start: 22px;
    font-family: 'Manrope', sans-serif;
    font-size: 13px;
    line-height: 1.4;
    color: var(--pm-tx);
  }

  li::before {
    content: '✓';
    position: absolute;
    inset-inline-start: 0;
    top: 0;
    font-weight: 700;
    color: var(--pm-gold, #d4a032);
  }
}

.pm-payment-mount {
  min-height: 40px;
}

.pm-paypal-mount {
  min-height: 0;
  width: 100%;

  // PayPal renders its buttons into a zoid iframe; keep it full-width so the
  // two PayPal buttons line up with the full-width Stripe "Subscribe & pay".
  :deep(iframe),
  :deep(.paypal-buttons) {
    width: 100% !important;
    min-width: 100% !important;
  }
}

/* ── Checkout: loading / error ───────────────────────────────────────────── */
.pm-checkout-loading {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

.pm-spinner {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 3px solid var(--pm-gold-line);
  border-top-color: var(--pm-gold);
  animation: pm-spin 0.7s linear infinite;
}

@keyframes pm-spin {
  to {
    transform: rotate(360deg);
  }
}

.pm-checkout-error {
  text-align: center;
  padding: 28px 0;

  p {
    margin: 0 0 14px;
    font-size: 14px;
    color: var(--pm-tx2);
  }
}

.pm-retry-btn {
  font-size: 14px;
  font-weight: 700;
  padding: 10px 22px;
  border-radius: 11px;
  border: 1px solid var(--pm-bd2);
  background: transparent;
  color: var(--pm-tx);
  cursor: pointer;
  font-family: 'Manrope', sans-serif;

  &:hover {
    background: var(--pm-bd);
  }
}

/* ── Checkout: pay button + errors ───────────────────────────────────────── */
.pm-pay-error {
  margin: 12px 0 0;
  font-size: 12.5px;
  color: #c0392b;
}

.pm-pay-btn {
  display: block;
  width: 100%;
  margin-top: 20px;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  padding: 15px 18px;
  border-radius: 12px;
  border: none;
  background: var(--pm-gold);
  color: var(--pm-on-gold);
  cursor: pointer;
  font-family: 'Manrope', sans-serif;
  transition:
    background 0.15s,
    opacity 0.15s;

  &:hover:not(:disabled) {
    background: #eda436;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.pm-pay-secure {
  margin: 12px 0 0;
  text-align: center;
  font-size: 11.5px;
  color: var(--pm-mut);
}

.pm-or {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0 16px;
}

.pm-or-line {
  height: 1px;
  flex: 1;
  background: var(--pm-bd);
}

.pm-or-txt {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--pm-mut);
}

.pm-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
  font-size: 14px;
  font-weight: 600;
  color: var(--pm-tx2);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-family: 'Manrope', sans-serif;
  transition: color 0.15s;

  &:hover:not(:disabled) {
    color: var(--pm-tx);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

/* ── Success step (placeholder) ──────────────────────────────────────────── */
.pm-success {
  text-align: center;
  padding: 64px 0;
}

.pm-success-icon {
  color: var(--pm-gold);
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}

.pm-success-title {
  font-family: 'Fraunces', serif;
  font-weight: 600;
  font-size: 28px;
  color: var(--pm-tx);
  margin: 0 0 12px;
}

.pm-success-sub {
  font-size: 15px;
  color: var(--pm-tx2);
  margin: 0 0 32px;
}

.pm-success-btn {
  display: inline-block;
  background: var(--pm-gold);
  color: var(--pm-on-gold);
  font-size: 15px;
  font-weight: 700;
  padding: 14px 36px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  font-family: 'Manrope', sans-serif;
  transition: background 0.15s;

  &:hover {
    background: #eda436;
  }
}
</style>
